import { $api } from "@/kibi/core/utils/api";
import type {
  PaginatedResponse,
  QueryOptions,
} from "@/kibi/core/utils/pagination_types";
import type { InjectionKey } from "vue";
import type { Batch } from "../../domain/entities/batch";
import type {
  Country,
  Product,
  UpdateProductRequest,
} from "../../domain/entities/product";

export interface ProductDataSource {
  getProducts(options: QueryOptions): Promise<PaginatedResponse<Product[]>>;
  getProduct(id: string): Promise<Product>;
  createProduct(request: FormData): Promise<Product>;
  updateProduct(id: string, request: UpdateProductRequest): Promise<Product>;
  deleteProduct(id: string): Promise<void>;
  updateProductImage(id: string, file: File): Promise<string>;
  removeProductImage(id: string): Promise<void>;
  getCountries(): Promise<Country[]>;
  getBatchesByProduct(productId: string): Promise<Batch[]>;
}

export const productDataSourceKey: InjectionKey<ProductDataSource> =
  Symbol("ProductDataSource");

export class ProductDataSourceImpl implements ProductDataSource {
  async getProducts(
    options: QueryOptions,
  ): Promise<PaginatedResponse<Product[]>> {
    const response = await $api<PaginatedResponse<Product[]>>("/products", {
      method: "GET",
      params: options,
    });
    return response;
  }

  async getProduct(id: string): Promise<Product> {
    const response = await $api<{ data: Product }>(`/products/${id}`, {
      method: "GET",
    });
    return response.data;
  }

  async createProduct(request: FormData): Promise<Product> {
    const response = await $api<{ data: Product }>("/products", {
      method: "POST",
      body: request,
    });
    return response.data;
  }

  async updateProduct(
    id: string,
    request: UpdateProductRequest,
  ): Promise<Product> {
    const response = await $api<{ data: Product }>(`/products/${id}`, {
      method: "PUT",
      body: request,
    });
    return response.data;
  }

  async deleteProduct(id: string): Promise<void> {
    await $api(`/products/${id}`, {
      method: "DELETE",
    });
  }

  async updateProductImage(id: string, file: File): Promise<string> {
    const formData = new FormData();
    formData.append("image", file);
    const response = await $api<{ data: string }>(`/products/${id}/image`, {
      method: "PUT",
      body: formData,
    });
    return response.data;
  }

  async removeProductImage(id: string): Promise<void> {
    await $api(`/products/${id}/image`, {
      method: "DELETE",
    });
  }

  async getCountries(): Promise<Country[]> {
    const response = await $api<{ data: Country[] }>("/accounts/countries", {
      method: "GET",
    });
    return response.data;
  }

  async getBatchesByProduct(productId: string): Promise<Batch[]> {
    const response = await $api<{ data: Batch[] }>(
      `/products/${productId}/batches`,
      {
        method: "GET",
      },
    );
    return response.data;
  }
}
